// ═══════════════════════════════════════════════════════════════════════════════
// Platform Announcements — Push notices to all tenant admins
//
// Full CRUD with table display, type badges, status tracking,
// optional email dispatch, and create/edit/delete modals.
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, Plus, Pencil, Trash2, Info, AlertTriangle, Wrench, Sparkles,
  Clock, Mail,
} from 'lucide-react';
import { Modal, Toggle, Spinner } from '@/components/shared/UIComponents';
import {
  fetchPlatformAnnouncements,
  createPlatformAnnouncement,
  updatePlatformAnnouncement,
  deletePlatformAnnouncement,
  sendPlatformAnnouncementEmail,
} from '@/lib/api';
import { cn } from '@/utils/helpers';
import type {
  PlatformAnnouncement,
  PlatformAnnouncementType,
  CreatePlatformAnnouncementInput,
  UpdatePlatformAnnouncementInput,
} from '@/types';

// ── Type config ──────────────────────────────────────────────────────────────

const TYPE_OPTIONS: {
  value: PlatformAnnouncementType;
  label: string;
  icon: typeof Info;
  color: string;
  bg: string;
}[] = [
  { value: 'info',        label: 'Info',        icon: Info,           color: 'text-blue-700 dark:text-blue-400',   bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/40' },
  { value: 'warning',     label: 'Warning',     icon: AlertTriangle,  color: 'text-amber-700 dark:text-amber-400',  bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/40' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench,         color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/40' },
  { value: 'feature',     label: 'Feature',     icon: Sparkles,       color: 'text-teal-700 dark:text-teal-400',   bg: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800/40' },
];

const TARGET_OPTIONS: { value: 'all' | 'active' | 'trial'; label: string }[] = [
  { value: 'all',    label: 'All Tenants' },
  { value: 'active', label: 'Active Only' },
  { value: 'trial',  label: 'Trial Only' },
];

function getTypeStyle(type: PlatformAnnouncementType) {
  return TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0];
}

function getTargetLabel(target: string) {
  return TARGET_OPTIONS.find(t => t.value === target)?.label || target;
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function defaultExpiry(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 16);
}

/** Sort: active first, then by created_at descending */
function sortAnnouncements(list: PlatformAnnouncement[]): PlatformAnnouncement[] {
  return [...list].sort((a, b) => {
    const aActive = a.is_active && !isExpired(a.expires_at);
    const bActive = b.is_active && !isExpired(b.expires_at);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

function getStatus(a: PlatformAnnouncement): { label: string; cls: string } {
  if (isExpired(a.expires_at)) return { label: 'Expired', cls: 'bg-surface-100 text-surface-500' };
  if (a.is_active) return { label: 'Active', cls: 'badge-emerald' };
  return { label: 'Draft', cls: 'bg-surface-100 text-surface-500' };
}

// ═══════════════════════════════════════════════════════════════════════════════

export function PlatformAnnouncementsPage() {
  const [announcements, setAnnouncements] = useState<PlatformAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PlatformAnnouncement | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<PlatformAnnouncement | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    content: '',
    type: 'info' as PlatformAnnouncementType,
    target: 'all' as 'all' | 'active' | 'trial',
    is_active: false,
    send_email: false,
    expires_at: '',
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPlatformAnnouncements();
      setAnnouncements(sortAnnouncements(data));
    } catch (err: any) {
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Modal helpers ──────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: '',
      content: '',
      type: 'info',
      target: 'all',
      is_active: false,
      send_email: false,
      expires_at: '',
    });
    setShowModal(true);
  };

  const openEdit = (a: PlatformAnnouncement) => {
    setEditing(a);
    setForm({
      title: a.title,
      content: a.content,
      type: a.type,
      target: a.target,
      is_active: a.is_active,
      send_email: false, // never pre-check "send email" on edit
      expires_at: a.expires_at ? new Date(a.expires_at).toISOString().slice(0, 16) : '',
    });
    setShowModal(true);
  };

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const input: UpdatePlatformAnnouncementInput = {
          title: form.title,
          content: form.content,
          type: form.type,
          target: form.target,
          is_active: form.is_active,
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        };
        const updated = await updatePlatformAnnouncement(editing.id, input);
        // If "send email" was checked, dispatch the email
        if (form.send_email) {
          await sendPlatformAnnouncementEmail(updated.id);
        }
        await load();
      } else {
        const input: CreatePlatformAnnouncementInput = {
          title: form.title,
          content: form.content,
          type: form.type,
          target: form.target,
          is_active: form.is_active,
          send_email: form.send_email,
          expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        };
        const created = await createPlatformAnnouncement(input);
        // If "send email" was checked, dispatch the email
        if (form.send_email) {
          await sendPlatformAnnouncementEmail(created.id);
        }
        await load();
      }
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save announcement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePlatformAnnouncement(deleteTarget.id);
      setAnnouncements(prev => prev.filter(a => a.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete announcement');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (a: PlatformAnnouncement) => {
    try {
      await updatePlatformAnnouncement(a.id, { is_active: !a.is_active });
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle announcement');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Platform Announcements</h1>
          <p className="text-surface-500 mt-1">Push notices to all tenant admins</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus size={16} /> New Announcement
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-3 banner-red border rounded-xl text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Announcements table */}
      {announcements.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="w-12 h-12 text-surface-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-surface-700 mb-1">No announcements yet</h3>
          <p className="text-sm text-surface-500 mb-6">
            Create a platform announcement to notify all tenant admins.
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={16} /> Create your first announcement
          </button>
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell">Title</th>
                <th className="table-header-cell w-28">Type</th>
                <th className="table-header-cell w-28">Target</th>
                <th className="table-header-cell w-24">Status</th>
                <th className="table-header-cell w-32">Email Sent</th>
                <th className="table-header-cell w-32">Created</th>
                <th className="table-header-cell w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {announcements.map(a => {
                const expired = isExpired(a.expires_at);
                const style = getTypeStyle(a.type);
                const TypeIcon = style.icon;
                const status = getStatus(a);

                return (
                  <tr key={a.id} className={cn('table-row', expired && 'opacity-50')}>
                    {/* Title */}
                    <td className="table-cell">
                      <div className="text-sm font-medium text-surface-900 truncate max-w-xs">{a.title}</div>
                      <div className="text-xs text-surface-500 truncate max-w-xs">{a.content}</div>
                    </td>

                    {/* Type badge */}
                    <td className="table-cell">
                      <span className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border',
                        style.bg, style.color,
                      )}>
                        <TypeIcon size={12} />
                        {style.label}
                      </span>
                    </td>

                    {/* Target */}
                    <td className="table-cell">
                      <span className="text-xs text-surface-600">{getTargetLabel(a.target)}</span>
                    </td>

                    {/* Status */}
                    <td className="table-cell">
                      <span className={cn('text-xs px-2 py-0.5 rounded-md font-medium', status.cls)}>
                        {status.label}
                      </span>
                    </td>

                    {/* Email Sent */}
                    <td className="table-cell">
                      {a.email_sent_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-surface-600">
                          <Mail size={12} className="text-surface-400" />
                          {formatDate(a.email_sent_at)}
                        </span>
                      ) : (
                        <span className="text-xs text-surface-400">&mdash;</span>
                      )}
                    </td>

                    {/* Created */}
                    <td className="table-cell">
                      <span className="text-xs text-surface-600">{formatDate(a.created_at)}</span>
                    </td>

                    {/* Actions */}
                    <td className="table-cell">
                      <div className="flex items-center justify-end gap-2">
                        {!expired && (
                          <Toggle
                            enabled={a.is_active}
                            onChange={() => handleToggleActive(a)}
                          />
                        )}
                        <button
                          onClick={() => openEdit(a)}
                          className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(a)}
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/20 text-surface-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      {showModal && (
        <Modal
          open={showModal}
          title={editing ? 'Edit Announcement' : 'New Announcement'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Scheduled maintenance on March 15"
                maxLength={120}
                className="input-field text-sm"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Content</label>
              <textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Write the announcement content..."
                rows={4}
                maxLength={2000}
                className="input-field text-sm resize-none"
              />
              <div className="text-xs text-surface-400 text-right mt-1">{form.content.length}/2000</div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Type</label>
              <div className="flex flex-wrap gap-2">
                {TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                      className={cn(
                        'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all',
                        form.type === opt.value
                          ? `${opt.bg} ${opt.color} ring-2 ring-offset-1 ring-current`
                          : 'border-surface-200 text-surface-600 hover:border-surface-300',
                      )}
                    >
                      <Icon size={14} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Target */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Target</label>
              <select
                value={form.target}
                onChange={e => setForm(f => ({ ...f, target: e.target.value as 'all' | 'active' | 'trial' }))}
                className="input-field text-sm"
              >
                {TARGET_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Expires at */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Expires at (optional)</label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                min={new Date().toISOString().slice(0, 16)}
                className="input-field text-sm"
              />
              <p className="text-xs text-surface-400 mt-1">
                Leave empty for no expiration, or set a date to auto-expire.
              </p>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium text-surface-700">Publish immediately</div>
                <div className="text-xs text-surface-400">Make this announcement visible to tenant admins now</div>
              </div>
              <Toggle
                enabled={form.is_active}
                onChange={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
              />
            </div>

            {/* Send as email */}
            <div className="border-t border-surface-200 pt-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_email}
                  onChange={e => setForm(f => ({ ...f, send_email: e.target.checked }))}
                  className="mt-0.5 rounded border-surface-300 text-brand-500 focus:ring-brand-500"
                />
                <div>
                  <div className="text-sm font-medium text-surface-700">Send as Email</div>
                  {form.send_email && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      An email will be sent to all tenant admins matching the target filter.
                    </p>
                  )}
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.content.trim()}
                className="px-5 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation ────────────────────────────────────────────── */}
      {deleteTarget && (
        <Modal
          open={!!deleteTarget}
          title="Delete Announcement"
          onClose={() => setDeleteTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-surface-600">
              Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
